import { useEffect, useRef, useState } from "react";
import Markdown from "react-markdown";
import "./text-stream.css";

import { useWatchChange } from "~/util/hooks";

const recursivelyPrepareForTextStreamAnimation = <T extends HTMLElement>(
  element: T,
) => {
  if (!element) return;

  if (element.nodeType === Node.TEXT_NODE && element.textContent?.trim()) {
    const parent = element.parentNode;
    if (!parent) return;

    const words = element.textContent.split(/(\s+)/);

    const fragment = document.createDocumentFragment();
    words.forEach((word, index) => {
      if (word.trim()) {
        const span = document.createElement("span");
        span.textContent = word;
        span.style.opacity = "0";
        span.style.transition = "opacity 150ms ease-in-out";
        span.dataset.animationIndex = index.toString();
        fragment.appendChild(span);
      } else {
        fragment.appendChild(document.createTextNode(word));
      }
    });

    parent.replaceChild(fragment, element);
    return;
  }

  if (element.nodeName === "LI") {
    element.classList.add("fade-marker");
  }

  if (element.childNodes && element.childNodes.length > 0) {
    Array.from(element.childNodes).forEach((child) => {
      recursivelyPrepareForTextStreamAnimation(child as HTMLElement);
    });
  }
};

export const MamaMarkdown: React.FC<{
  text?: string;
  stream?: boolean;
  step?: number;
  onComplete?: () => void;
}> = ({ text, stream = false, step = 30, onComplete = () => void 0 }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [hasProcessed, setHasProcessed] = useState(false);
  const [isReady, setIsReady] = useState(false);
  useWatchChange(text, () => setHasProcessed(false));

  useWatchChange(hasProcessed, (newValue) => {
    if (!stream || !newValue || !ref.current) {
      return;
    }

    recursivelyPrepareForTextStreamAnimation(ref.current);
    setIsReady(true);
  });

  useEffect(() => {
    if (!isReady || !ref.current) return;

    const words = ref.current.querySelectorAll<HTMLSpanElement>(
      "span[data-animation-index]",
    );
    words.forEach((word, index) => {
      setTimeout(() => {
        const relativeIndex = Number(
          word.getAttribute("data-animation-index") || -1,
        );
        if (relativeIndex == 0) {
          const li = word.closest("li");
          if (li) {
            li.classList.add("fade-in");
          }
        }
        word.style.opacity = "1";

        if (index === words.length - 1) {
          onComplete();
        }
      }, index * step);
    });
  }, [isReady, step, onComplete]);

  useEffect(() => {
    const processNodes = () => {
      if (!ref.current) return;

      const nodes: Node[] = [];
      const walker = document.createTreeWalker(
        ref.current,
        NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
        {
          acceptNode: (node) => {
            if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
              return NodeFilter.FILTER_ACCEPT;
            }
            if (
              node.nodeType === Node.ELEMENT_NODE &&
              node.childNodes.length === 0 &&
              !["BR", "HR"].includes((node as Element).tagName)
            ) {
              return NodeFilter.FILTER_ACCEPT;
            }
            return NodeFilter.FILTER_SKIP;
          },
        },
      );

      let currentNode;
      while ((currentNode = walker.nextNode())) {
        nodes.push(currentNode);
      }

      let lastOpeningSupIndex = -2;
      let lastClosingSupIndex = -1;
      nodes.forEach((node, index) => {
        if (node.nodeType === Node.TEXT_NODE && node.textContent === "<sup>") {
          node.parentElement?.removeChild(node);
          lastOpeningSupIndex = index;
        }

        if (node.nodeType === Node.TEXT_NODE && node.textContent === "</sup>") {
          node.parentElement?.removeChild(node);
          lastClosingSupIndex = index;
        }

        if (
          node.parentNode &&
          index > lastOpeningSupIndex &&
          (lastOpeningSupIndex >= lastClosingSupIndex ||
            index === lastClosingSupIndex - 1)
        ) {
          const newSup = document.createElement("sup");
          newSup.textContent = node.textContent?.trim() ?? "";
          node.parentNode.replaceChild(newSup, node);
          return;
        }
      });

      setHasProcessed(true);
    };

    processNodes();
  }, [text]);

  return (
    <div ref={ref} className="notranslate prose flex flex-col gap-0">
      <Markdown>{text}</Markdown>
    </div>
  );
};
